/**
 * ========================== RAPOR TARIH ARALIKLARI ==========================
 *
 * Iki ekran da tarih araligi ile calisiyor ama SUNUCU SOZLESMELERI FARKLI —
 * bu yuzden aralik uretimi tek yerde durur:
 *
 *   /v1/dashboards/exchange   `startDate` ve `endDate` ZORUNLU. SQL dogrudan
 *                             `t.created_at >= $2 AND t.created_at <= $3`
 *                             yaziyor; parametre null gelirse karsilastirma
 *                             NULL'a coker ve uc HICBIR SATIR donmez — yani
 *                             "aralik yok" demek "veri yok" demek olur.
 *
 *   /v1/reports/*             `from` / `to` OPSIYONEL; null gonderilirse
 *                             sunucu o siniri hic uygulamaz ("tum zamanlar").
 *                             Ama GECERSIZ bir tarih 400 doner — sunucu
 *                             bilincli olarak sessizce sinirsiza dusmuyor.
 *
 * Dosya adi `report-` onekli: src/lib altindaki paylasilan yardimcilar icin
 * uzerinde anlasilmis dosya sahipligi kurali.
 * ========================================================================== */

/**
 * SABIT ARALIKLAR — `labelKey` metin DEGIL CEVIRI ANAHTARI.
 *
 * Dizi modul seviyesinde: burada t() cagirmak metni IMPORT ANINDA dondurur ve
 * dil degistiginde bayat birakir. Alan adinin bunu okurken belli etmesi
 * gerekiyor (bkz. src/layout/menus.ts, src/features/organization/labels.ts).
 */
export type ReportRange = Readonly<{
  key: ReportRangeKey;
  labelKey: string;
  /**
   * Kac gun geriye; `null` = tum zamanlar (sunucuya `from` gonderilmez).
   * `ytd` bir GUN SAYISI DEGIL — takvim baslangicidir, bu yuzden `days` yerine
   * `startsAt` ile anlatilir (asagida). Ikisinden tam olarak biri dolu olur.
   */
  days?: number | null;
  /** Sabit takvim baslangici uretir ('ytd' icin 1 Ocak). */
  startsAt?: (now: Date) => Date;
}>;

/**
 * Aralik listesi MOBIL ILE AYNI (dovizhub-mobile/src/shared/constant/reportRanges.ts).
 * Iki istemci ayni pencereleri sunmali; listeler ayri yasarsa biri digerinden
 * sessizce ayrisir ve "son 90 gun" iki ekranda farkli sayilar gosterir.
 */
export const REPORT_RANGE_KEYS = ['30d', '90d', '1y', 'ytd', 'all'] as const;
export type ReportRangeKey = (typeof REPORT_RANGE_KEYS)[number];

export const REPORT_RANGES: readonly ReportRange[] = Object.freeze([
  Object.freeze({ key: '30d', labelKey: 'report.range30', days: 30 }),
  Object.freeze({ key: '90d', labelKey: 'report.range90', days: 90 }),
  Object.freeze({ key: '1y', labelKey: 'report.range1y', days: 365 }),
  Object.freeze({
    key: 'ytd',
    labelKey: 'report.rangeYtd',
    // Icinde bulunulan yilin 1 Ocak'i, YEREL saatle. UTC ile kurulsaydi TR'de
    // (UTC+3) yilin ilk gunu araligin disinda kalirdi.
    startsAt: (now: Date) => new Date(now.getFullYear(), 0, 1),
  }),
  Object.freeze({ key: 'all', labelKey: 'report.rangeAll', days: null }),
] as ReportRange[]);

export const findReportRange = (key: ReportRangeKey): ReportRange => {
  const found = REPORT_RANGES.find((range) => range.key === key);
  // REPORT_RANGE_KEYS ile REPORT_RANGES ayni listeden turuyor; yine de bir
  // gun ayrisirlarsa sessizce "tum zamanlar"a dusmek yanlis rapor demek olur.
  if (!found) throw new Error(`REPORT_RANGE_NOT_FOUND:${key}`);
  return found;
};

/**
 * Aralik -> `from` parametresi. `all` icin `undefined` (parametre HIC
 * gonderilmez; bos dize gondermek sunucuda INVALID_DATE_FROM uretirdi).
 */
export const rangeFromParam = (range: ReportRange, now: Date = new Date()): string | undefined =>
  rangeStart(range, now)?.toISOString();

/**
 * Araligin BASLANGIC TARIHI (`null` = sinirsiz).
 *
 * `rangeFromParam` bunun ISO'ya cevrilmis hali; Ozet ekrani `Date` istiyor
 * (tarih girdilerini doldurmak ve onceki donemi hesaplamak icin), Rapor ekrani
 * sorgu parametresi istiyor. Tek hesap, iki bicim.
 */
export const rangeStart = (range: ReportRange, now: Date = new Date()): Date | null => {
  if (range.startsAt) return range.startsAt(now);
  if (range.days === null || range.days === undefined) return null;
  const from = new Date(now.getTime());
  from.setDate(from.getDate() - range.days);
  return from;
};

/* -------------------------------------------------------------------------- */
/* Panel: gun bazli aralik                                                     */
/* -------------------------------------------------------------------------- */

/**
 * <input type="date"> `YYYY-MM-DD` okur/yazar. YEREL takvim gunu olarak
 * uretilir: `toISOString().slice(0,10)` yazmak TR'de (UTC+3) gece yarisindan
 * once secilen gunu BIR GUN GERI kaydirirdi.
 */
export const toDateInputValue = (date: Date): string => {
  const year = String(date.getFullYear()).padStart(4, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** `YYYY-MM-DD` -> yerel takvim gunu. Gecersizse `null` (cagri yeri karar verir). */
export const fromDateInputValue = (value: string): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const [, year, month, day] = match;
  if (year === undefined || month === undefined || day === undefined) return null;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * Baslangic gunu -> gunun BASI, bitis gunu -> gunun SONU.
 *
 * Sunucu `t.created_at <= $3` yaziyor. Bitis icin gun basi (00:00) gonderilse
 * secilen GUNUN KENDISI aralik disinda kalirdi — kullanici "bugune kadar"
 * secip bugunku islemleri goremezdi.
 */
export const startOfDayIso = (date: Date): string =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0).toISOString();

export const endOfDayIso = (date: Date): string =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999).toISOString();

/** Panelin varsayilan araligi: SON 1 YIL (mobil ekranla ayni). */
/**
 * Panelin varsayilan araligi: SON 30 GUN (mobil ekranla ayni).
 *
 * ESKIDEN 1 YILDI. Mobil bunu bilincli olarak degistirdi ("cipsiz ve keyfi bir
 * pencereydi") ve web'e hizli aralik cipleri geldiginde ayni sorun burada da
 * gorunur oldu: cip "Son 30 gun"u secili gosterirken tarih alanlari bir yillik
 * pencere yaziyordu — iki kontrol ayni durumu FARKLI anlatiyordu.
 *
 * Deger DEFAULT_DASHBOARD_RANGE_KEY'den turetiliyor, elle yazilmiyor: cip ile
 * varsayilan pencere tek kaynaktan gelsin.
 */
export const DEFAULT_DASHBOARD_RANGE_KEY: ReportRangeKey = '30d';

export const defaultDashboardRange = (now: Date = new Date()): { start: Date; end: Date } => {
  const start = rangeStart(findReportRange(DEFAULT_DASHBOARD_RANGE_KEY), now);
  // '30d' sinirli bir aralik; `rangeStart` yalniz 'all' icin null doner.
  return { start: start ?? now, end: now };
};

/** tr-TR gun bicimi — Intl, elle kurulmus bir sablon degil. */
const dayFormatter = new Intl.DateTimeFormat('tr-TR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

export const formatDay = (date: Date): string => dayFormatter.format(date);
